import { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { Headphones, Bot, X, Send, ExternalLink, UserRound } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { api } from '@/lib/api'

// ── 語系偵測（簡化版自 AiChatPage） ─────────────────────────────
function detectScript(text: string): 'cjk-han' | 'hangul' | 'kana' | 'latin' | 'other' {
  for (const ch of text.slice(0, 200)) {
    const c = ch.codePointAt(0) ?? 0
    if (c >= 0xAC00 && c <= 0xD7AF) return 'hangul'
    if ((c >= 0x3040 && c <= 0x309F) || (c >= 0x30A0 && c <= 0x30FF)) return 'kana'
    if (c >= 0x4E00 && c <= 0x9FFF) return 'cjk-han'
    if ((c >= 0x0041 && c <= 0x007A) || (c >= 0x00C0 && c <= 0x024F)) return 'latin'
  }
  return 'other'
}
function langToScript(lang: string): 'cjk-han' | 'hangul' | 'kana' | 'latin' {
  if (lang.startsWith('zh')) return 'cjk-han'
  if (lang === 'ja') return 'kana'
  if (lang === 'ko') return 'hangul'
  return 'latin'
}
function isSameScript(text: string, navLang: string): boolean {
  const a = detectScript(text), b = langToScript(navLang)
  // ja 文常含 cjk-han（漢字）— 視為同 script；zh 反之
  if (b === 'kana' && (a === 'kana' || a === 'cjk-han')) return true
  if (b === 'cjk-han' && (a === 'cjk-han' || a === 'kana')) return true
  return a === b
}

// ─────────────────────────────────────────
// 型別定義
// ─────────────────────────────────────────

interface AffiliateLink {
  platform: string
  label: string
  url: string
  icon: string
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  time: Date
  affiliate_links?: AffiliateLink[]
  /** #64：true = 系統訊息（轉介事件） */
  is_handoff?: boolean
  /** #64：若為真人客服回覆，帶客服 display_name */
  agent_name?: string
}

/** #64：客服值機狀態 */
interface CsStatus {
  business_hours_open: boolean
  oncall_count: number
  can_handoff: boolean
}

// ─────────────────────────────────────────
// 工具函式
// ─────────────────────────────────────────

/** 將 Date 格式化為 HH:mm（24 小時制） */
const formatTime = (d: Date) =>
  d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false })

/** 產生唯一 ID */
const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

// 歡迎訊息改為 component 內依 i18n 動態建立（見 useState 初始化）
const WELCOME_ID = 'welcome'

// ─────────────────────────────────────────
// 載入動畫（三個跳動的像素點）
// ─────────────────────────────────────────
function LoadingDots() {
  return (
    <div className="flex items-end gap-[3px] px-3 py-2">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="block w-[6px] h-[6px] bg-[var(--color-border)] animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  )
}

// ─────────────────────────────────────────
// 主元件
// ─────────────────────────────────────────
export function SupportChat() {
  const { t, i18n: i18nInst } = useTranslation('support')
  const { user } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const navLang = i18nInst.language || 'zh-TW'
  const [messages, setMessages] = useState<Message[]>(() => [{
    id: WELCOME_ID, role: 'assistant', content: t('welcome'), time: new Date(),
  }])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // D2 post-translate 結果：key = `${msgId}::${targetLang}`
  const [translations, setTranslations] = useState<Record<string, { status: 'pending' | 'done' | 'error'; text?: string }>>({})
  // #64：客服值機狀態
  const [csStatus, setCsStatus] = useState<CsStatus | null>(null)
  // #64：當前用戶是否處於「已轉介給真人客服」狀態
  const [handoffActive, setHandoffActive] = useState(false)
  // #64：handoff loading（按鈕點擊期間）
  const [handoffLoading, setHandoffLoading] = useState(false)
  // #64：handoff 確認 Modal（取代 window.confirm — 站內統一 UI）
  const [showHandoffConfirm, setShowHandoffConfirm] = useState(false)

  // 切語系時，重新生成 welcome（如果是當前 welcome 訊息）
  useEffect(() => {
    setMessages((prev) => prev.map((m) =>
      m.id === WELCOME_ID ? { ...m, content: t('welcome') } : m
    ))
    setTranslations({})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navLang])

  // 開啟 SupportChat 時載入歷史對話（最近 30 筆）— 僅登入後 + 第一次開啟
  const [historyLoaded, setHistoryLoaded] = useState(false)
  useEffect(() => {
    if (!isOpen || !user || historyLoaded) return
    let cancelled = false
    api.get('api/support/chat/history').json<{ messages: Array<{ role: 'user' | 'assistant'; content: string; created_at: string; is_handoff?: boolean; agent_name?: string }> }>()
      .then((data) => {
        if (cancelled) return
        const restored: Message[] = data.messages.map((m, i) => ({
          id: `hist-${i}-${Date.parse(m.created_at)}`,
          role: m.role,
          content: m.content,
          time: new Date(m.created_at),
          is_handoff: m.is_handoff,
          agent_name: m.agent_name,
        }))
        // #64：判斷是否仍處於 handoff 狀態（24h 內有 is_handoff 訊息）
        const now = Date.now()
        const stillHandoff = data.messages.some((m) =>
          m.is_handoff && now - Date.parse(m.created_at) < 24 * 3600 * 1000
        )
        if (stillHandoff) setHandoffActive(true)
        if (restored.length > 0) {
          // 把歷史插在 welcome 之後（welcome 永遠保留為第一筆）
          setMessages((prev) => {
            const welcome = prev.find((m) => m.id === WELCOME_ID)
            return welcome ? [welcome, ...restored] : restored
          })
        }
        setHistoryLoaded(true)
      })
      .catch(() => setHistoryLoaded(true))
    return () => { cancelled = true }
  }, [isOpen, user, historyLoaded])

  // #64：開啟 SupportChat 時 fetch cs-status + 每 60 秒重新 fetch
  useEffect(() => {
    if (!isOpen || !user) return
    let cancelled = false
    const fetchCsStatus = () => {
      api.get('api/support/cs-status').json<CsStatus>()
        .then((data) => { if (!cancelled) setCsStatus(data) })
        .catch(() => {})
    }
    fetchCsStatus()
    const id = setInterval(fetchCsStatus, 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [isOpen, user])

  // #64：handoff active 時，每 5 秒 polling history 拉新訊息（客服回覆）
  useEffect(() => {
    if (!handoffActive || !isOpen || !user) return
    let cancelled = false
    const id = setInterval(() => {
      api.get('api/support/chat/history').json<{ messages: Array<{ role: 'user' | 'assistant'; content: string; created_at: string; is_handoff?: boolean; agent_name?: string }> }>()
        .then((data) => {
          if (cancelled) return
          const restored: Message[] = data.messages.map((m, i) => ({
            id: `hist-${i}-${Date.parse(m.created_at)}`,
            role: m.role,
            content: m.content,
            time: new Date(m.created_at),
            is_handoff: m.is_handoff,
            agent_name: m.agent_name,
          }))
          setMessages((prev) => {
            const welcome = prev.find((m) => m.id === WELCOME_ID)
            return welcome ? [welcome, ...restored] : restored
          })
        })
        .catch(() => {})
    }, 5_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [handoffActive, isOpen, user])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // ── WebSocket refs ──
  const wsRef = useRef<WebSocket | null>(null)
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const streamingIdRef = useRef<string | null>(null)
  // 儲存最新 messages 供 handleSend 讀取，避免閉包過期
  const messagesRef = useRef(messages)
  useEffect(() => { messagesRef.current = messages }, [messages])

  // 捲動到最新訊息（必須在條件式 return 之前呼叫，符合 Rules of Hooks）
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // ── WebSocket 連線 ──
  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return
    // H1 cookie 認證；不需 token query
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws/ai`
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }))
      }, 25_000)
    }

    ws.onclose = () => {
      if (pingTimer.current) clearInterval(pingTimer.current)
    }

    ws.onerror = () => ws.close()

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data as string)
      if (data.type === 'pong') return

      if (data.type === 'token') {
        const delta: string = data.delta
        setMessages((prev) => {
          const sid = streamingIdRef.current
          if (!sid) {
            const newId = genId()
            streamingIdRef.current = newId
            // 移除 typing placeholder，建立串流訊息泡
            return [...prev.filter((m) => m.id !== '__typing__'), {
              id: newId, role: 'assistant', content: delta, time: new Date()
            }]
          }
          return prev.map((m) => m.id === sid ? { ...m, content: m.content + delta } : m)
        })
        return
      }

      if (data.type === 'done') {
        const sid = streamingIdRef.current
        streamingIdRef.current = null
        // #64：handoff active 路徑沒走 stream（無 sid），需新增訊息而非 update
        if (data.handoff_active) {
          setHandoffActive(true)
          setMessages((prev) => [...prev.filter((m) => m.id !== '__typing__'), {
            id: genId(),
            role: 'assistant',
            content: data.answer,
            time: new Date(),
          }])
        } else if (sid) {
          setMessages((prev) =>
            prev.map((m) => m.id === sid
              ? { ...m, content: data.answer, affiliate_links: data.affiliate_links?.length ? data.affiliate_links : undefined }
              : m
            )
          )
        }
        setIsLoading(false)
        return
      }

      if (data.type === 'error') {
        streamingIdRef.current = null
        setMessages((prev) => [...prev.filter((m) => m.id !== '__typing__'), {
          id: genId(), role: 'assistant', content: t('error_no_response'), time: new Date()
        }])
        setIsLoading(false)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // isOpen 開啟時建立連線，關閉時中斷連線
  useEffect(() => {
    if (isOpen && user) connectWs()
    return () => {
      if (!isOpen) {
        wsRef.current?.close()
        wsRef.current = null
        if (pingTimer.current) clearInterval(pingTimer.current)
      }
    }
  }, [isOpen, user, connectWs])

  // ── D2 自動 post-translate：assistant 訊息語系 ≠ navLang 時翻譯 ──
  useEffect(() => {
    if (isLoading) return
    const targets = messages.filter((m) =>
      m.role === 'assistant' && m.id !== '__typing__' && m.content && m.content.trim().length > 0
    )
    targets.forEach((m) => {
      if (isSameScript(m.content, navLang)) return
      const key = `${m.id}::${navLang}`
      if (translations[key]) return
      setTranslations((prev) => ({ ...prev, [key]: { status: 'pending' } }))
      api.post('/api/ai/translate', {
        json: { text: m.content, target_lang: navLang },
        timeout: 60_000,
      }).json<{ translated: string }>()
        .then((res) => setTranslations((prev) => ({ ...prev, [key]: { status: 'done', text: res.translated } })))
        .catch(() => setTranslations((prev) => ({ ...prev, [key]: { status: 'error' } })))
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, navLang, isLoading])

  // 僅登入使用者才顯示（H1 cookie 化後改檢查 user）
  if (!user) return null

  // ── 送出訊息 ────────────────────────────
  const handleSend = () => {
    const trimmed = input.trim()
    if (!trimmed || isLoading) return

    const userMsg: Message = {
      id: genId(),
      role: 'user',
      content: trimmed,
      time: new Date(),
    }

    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setIsLoading(true)
    streamingIdRef.current = null
    textareaRef.current?.focus()

    // 取最近 6 則歷史（含剛加入的 user 訊息）
    const history = [...messagesRef.current, userMsg]
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.content }))

    const sendPayload = () => {
      wsRef.current?.send(JSON.stringify({ type: 'support', message: trimmed, history }))
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // WS 尚未開啟，先連線再送出
      connectWs()
      setTimeout(sendPayload, 500)
    } else {
      sendPayload()
    }
  }

  // ── 鍵盤事件：Enter 送出，Shift+Enter 換行 ──
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // #64：觸發轉接真人客服（按鈕 → 開 Modal；Modal 確認鈕 → confirmHandoff）
  const handleHandoff = () => {
    if (handoffLoading || handoffActive) return
    if (!csStatus?.can_handoff) return
    setShowHandoffConfirm(true)
  }

  const confirmHandoff = async () => {
    setShowHandoffConfirm(false)
    setHandoffLoading(true)
    try {
      const res = await api.post('api/support/handoff').json<{
        success: boolean; agent_id: string; agent_name: string; message: string
      }>()
      // 把 handoff system message 加入 messages（後端已寫 DB，前端先樂觀加上）
      setMessages((prev) => [...prev, {
        id: genId(),
        role: 'assistant',
        content: res.message,
        time: new Date(),
        is_handoff: true,
        agent_name: res.agent_name,
      }])
      setHandoffActive(true)
    } catch {
      setMessages((prev) => [...prev, {
        id: genId(),
        role: 'assistant',
        content: t('handoff_failed'),
        time: new Date(),
      }])
    } finally {
      setHandoffLoading(false)
    }
  }

  // #64：handoff banner 文字
  const handoffStatusText = (() => {
    if (!csStatus) return ''
    if (!csStatus.business_hours_open) return t('cs_status_offhours')
    if (csStatus.oncall_count === 0) return t('cs_status_no_oncall')
    return t('cs_status_oncall', { count: csStatus.oncall_count })
  })()

  // ─────────────────────────────────────────
  // 渲染
  // ─────────────────────────────────────────
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* ── 聊天面板 ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className={[
              'flex flex-col',
              'w-80 h-[480px]',
              'w-[calc(100vw-2rem)] sm:w-80', // 行動版全寬
              'bg-[var(--color-surface)]',
              'border-2 border-[var(--color-border)]',
              'shadow-[4px_4px_0_0_var(--color-border)]',
              'overflow-hidden',
            ].join(' ')}
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-3 py-2 bg-[var(--color-navy)] border-b-2 border-[var(--color-border)] shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Headphones className="w-4 h-4 text-[var(--color-sky)] flex-shrink-0" />
                <span className="font-pixel text-[9px] text-[var(--color-sky)] tracking-wider truncate">
                  {t('title')}
                </span>
                {/* 在線綠點 */}
                <span className="block w-2 h-2 rounded-full bg-[var(--color-mint)] animate-pulse flex-shrink-0" />
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className={[
                  'flex items-center justify-center w-6 h-6',
                  'border-2 border-[var(--color-border)]',
                  'bg-[var(--color-coral)] text-white',
                  'shadow-[2px_2px_0_0_var(--color-border)]',
                  'active:translate-x-[2px] active:translate-y-[2px] active:shadow-none',
                  'transition-transform',
                ].join(' ')}
                aria-label={t('close_aria')}
              >
                <X className="w-3 h-3" />
              </button>
            </div>

            {/* ── Sub-header banner: AI 提示 + 替代連結 ── */}
            <div className="px-3 py-1.5 bg-[var(--color-muted-bg)] border-b border-[var(--color-border)] text-[10px] text-[var(--color-muted)] flex items-center justify-between gap-2 shrink-0">
              <span className="flex-shrink-0">{t('footer_disclaimer')}</span>
              <div className="flex items-center gap-2 truncate">
                <Link to="/ai/chat" onClick={() => setIsOpen(false)} className="text-[var(--color-sky)] hover:underline truncate">
                  {t('footer_link_chat')}
                </Link>
                <span className="opacity-50">·</span>
                {/* #64：「轉接真人客服」按鈕 — 僅 can_handoff && !handoffActive 時可按 */}
                <button
                  onClick={handleHandoff}
                  disabled={!csStatus?.can_handoff || handoffActive || handoffLoading}
                  className={[
                    'inline-flex items-center gap-1 px-1.5 py-0.5 border',
                    csStatus?.can_handoff && !handoffActive
                      ? 'border-[var(--color-coral)] text-[var(--color-coral)] hover:bg-[var(--color-coral)]/10'
                      : 'border-[var(--color-border)] text-[var(--color-border)] cursor-not-allowed opacity-60',
                  ].join(' ')}
                  title={handoffStatusText}
                >
                  <UserRound className="w-2.5 h-2.5" />
                  <span>{handoffActive ? t('handoff_in_progress') : t('handoff_button')}</span>
                </button>
              </div>
            </div>

            {/* #64：handoff active banner — 已轉介給客服 */}
            {handoffActive && (
              <div className="px-3 py-1.5 bg-[var(--color-coral)]/15 border-b-2 border-[var(--color-coral)] text-[10px] text-[var(--color-foreground)] flex items-center gap-2 shrink-0">
                <UserRound className="w-3 h-3 text-[var(--color-coral)] shrink-0" />
                <span className="font-pixel text-[8px] tracking-wider">{t('handoff_active_banner')}</span>
              </div>
            )}

            {/* ── 訊息捲動區 ── */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
              <AnimatePresence initial={false}>
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, x: msg.role === 'user' ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`flex items-end gap-1.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    {/* assistant 左側 Bot 圖示 */}
                    {msg.role === 'assistant' && (
                      <Bot className="w-5 h-5 text-[var(--color-sky)] shrink-0 mb-[2px]" />
                    )}

                    <div className="flex flex-col gap-0.5 max-w-[75%]">
                      <div
                        className={[
                          'px-3 py-2 border-2 text-sm leading-relaxed whitespace-pre-wrap break-words',
                          msg.role === 'user'
                            ? 'bg-[var(--color-sky)]/20 border-[var(--color-sky)] text-[var(--color-foreground)]'
                            : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-foreground)]',
                        ].join(' ')}
                      >
                        {msg.content}
                      </div>

                      {/* D2 自動翻譯區塊（assistant 訊息語系 ≠ navLang 時顯示翻譯版） */}
                      {msg.role === 'assistant' && !isSameScript(msg.content, navLang) && (() => {
                        const tx = translations[`${msg.id}::${navLang}`]
                        if (!tx) return null
                        return (
                          <div className="mt-1 px-2 py-1 border border-dashed border-[var(--color-sky)] bg-[var(--color-sky)]/5 text-[12px] leading-relaxed whitespace-pre-wrap break-words text-[var(--color-foreground)]/85">
                            {tx.status === 'pending' && <span className="text-[10px] opacity-70">⋯ {t('translating')}</span>}
                            {tx.status === 'done' && tx.text}
                            {tx.status === 'error' && <span className="text-[10px] text-[var(--color-coral)]">{t('translate_failed')}</span>}
                          </div>
                        )
                      })()}

                      {/* Affiliate 連結卡片（僅 assistant 訊息且有連結時顯示） */}
                      {msg.role === 'assistant' && msg.affiliate_links && msg.affiliate_links.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {msg.affiliate_links.map((link) => (
                            <a
                              key={link.platform}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={[
                                'inline-flex items-center gap-1 px-2 py-1',
                                'border-2 border-[var(--color-border)]',
                                'shadow-[2px_2px_0_0_var(--color-border)]',
                                'bg-[var(--color-surface)]',
                                'text-[10px] text-[var(--color-foreground)]',
                                'hover:bg-[var(--color-sky)]/10',
                                'active:translate-x-[2px] active:translate-y-[2px] active:shadow-none',
                                'transition-transform',
                                'no-underline',
                              ].join(' ')}
                            >
                              <span>{link.icon}</span>
                              <span className="font-pixel text-[8px] leading-tight">{link.label}</span>
                              <ExternalLink className="w-2.5 h-2.5 opacity-60 shrink-0" />
                            </a>
                          ))}
                        </div>
                      )}

                      {/* 時間戳記 */}
                      <span
                        className={`font-pixel text-[8px] text-[var(--color-border)] ${msg.role === 'user' ? 'text-right' : 'text-left'}`}
                      >
                        {formatTime(msg.time)}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* 載入中跳動點 */}
              {isLoading && (
                <div className="flex items-end gap-1.5 flex-row">
                  <Bot className="w-5 h-5 text-[var(--color-sky)] shrink-0 mb-[2px]" />
                  <div className="border-2 border-[var(--color-border)] bg-[var(--color-surface)]">
                    <LoadingDots />
                  </div>
                </div>
              )}

              {/* 捲動錨點 */}
              <div ref={messagesEndRef} />
            </div>

            {/* ── 輸入區 ── */}
            <div className="flex items-end gap-2 px-3 py-2 border-t-2 border-[var(--color-border)] shrink-0 bg-[var(--color-surface)]">
              <textarea
                ref={textareaRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('placeholder')}
                disabled={isLoading}
                className={[
                  'flex-1 resize-none border-2 border-[var(--color-border)] bg-[var(--color-surface)]',
                  'text-sm text-[var(--color-foreground)] px-2 py-1.5',
                  'placeholder:text-[var(--color-border)] placeholder:text-xs',
                  'focus:outline-none focus:border-[var(--color-sky)]',
                  'disabled:opacity-50',
                  'leading-snug',
                ].join(' ')}
              />
              <button
                onClick={handleSend}
                disabled={isLoading || !input.trim()}
                className={[
                  'flex items-center justify-center w-9 h-9 shrink-0',
                  'border-2 border-[var(--color-border)]',
                  'bg-[var(--color-sky)] text-white',
                  'shadow-[2px_2px_0_0_var(--color-border)]',
                  'active:translate-x-[2px] active:translate-y-[2px] active:shadow-none',
                  'transition-transform',
                  'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:translate-x-0 disabled:active:translate-y-0 disabled:active:shadow-[2px_2px_0_0_var(--color-border)]',
                ].join(' ')}
                aria-label={t('send_aria')}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* #64：handoff 確認 Modal（取代 window.confirm） */}
      <AnimatePresence>
        {showHandoffConfirm && (
          <motion.div
            key="handoff-confirm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
            onClick={() => setShowHandoffConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 10 }}
              transition={{ type: 'spring', stiffness: 320, damping: 26 }}
              className="bg-[var(--color-surface)] border-2 border-[var(--color-border)] shadow-[4px_4px_0_0_var(--color-border)] p-4 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 mb-3">
                <UserRound className="w-4 h-4 text-[var(--color-coral)]" />
                <h3 className="font-pixel text-[9px] text-[var(--color-foreground)] tracking-wider">
                  {t('handoff_button')}
                </h3>
              </div>
              <p className="text-sm text-[var(--color-foreground)] mb-4 leading-relaxed">
                {t('handoff_confirm')}
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowHandoffConfirm(false)}
                  className="px-3 py-1.5 border-2 border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-foreground)] text-xs shadow-[2px_2px_0_0_var(--color-border)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
                >
                  {t('confirm_cancel')}
                </button>
                <button
                  onClick={confirmHandoff}
                  className="px-3 py-1.5 border-2 border-[var(--color-border)] bg-[var(--color-coral)] text-white text-xs shadow-[2px_2px_0_0_var(--color-border)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
                >
                  {t('confirm_ok')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── 懸浮按鈕（Closed 狀態） ── */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={[
          'relative flex items-center justify-center',
          'w-14 h-14',
          'bg-[var(--color-sky)]',
          'border-2 border-[var(--color-border)]',
          'shadow-[4px_4px_0_0_var(--color-border)]',
          'active:translate-x-[2px] active:translate-y-[2px] active:shadow-none',
          'transition-transform',
        ].join(' ')}
        aria-label={t('open_aria')}
      >
        <Headphones className="w-6 h-6 text-white" />
        {/* 在線紅點脈衝提示 */}
        {!isOpen && (
          <span className="absolute top-1 right-1 block w-3 h-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--color-coral)] opacity-75" />
            <span className="relative block w-3 h-3 rounded-full bg-[var(--color-coral)]" />
          </span>
        )}
      </button>
    </div>
  )
}
